import { ChevronDownIcon } from "@chakra-ui/icons";
import {
  Box,
  Button,
  CircularProgress,
  Flex,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverCloseButton,
  PopoverContent,
  PopoverHeader,
  PopoverTrigger,
  Text,
} from "@chakra-ui/react";
import { useState } from "react";
import { BiBlock } from "react-icons/bi";
import { FiThumbsDown, FiThumbsUp } from "react-icons/fi";
import { FormattedMessage, useIntl } from "react-intl";

import {
  SCENARIO_REVIEW_FIELDS,
  Scenario,
  ScenarioReview,
  ScenarioReviewObject,
  useUpdateScenarioStatus,
} from "@nens/ldo/api-v1";

import { EditSingleQualityControlItem } from "./edit-single-quality-control-item";
import { PartialScenarioReview } from "./quality-control-types";
import { SingleQualityControlItem } from "./single-quality-control-item";

interface QualityControlProps {
  scenario: Scenario;
  editable: boolean;
  reviewObject: ScenarioReviewObject | null;
}

function numTrue(review: PartialScenarioReview): number {
  return (
    (review.content_quality.ok ? 1 : 0) +
    (review.data_valid.ok ? 1 : 0) +
    (review.metadata_complete.ok ? 1 : 0) +
    (review.metadata_valid.ok ? 1 : 0)
  );
}

function numChecked(review: PartialScenarioReview) {
  return (
    (typeof review.content_quality.ok === "boolean" ? 1 : 0) +
    (typeof review.data_valid.ok === "boolean" ? 1 : 0) +
    (typeof review.metadata_complete.ok === "boolean" ? 1 : 0) +
    (typeof review.metadata_valid.ok === "boolean" ? 1 : 0)
  );
}

function allChecked(review: PartialScenarioReview): review is ScenarioReview {
  return numChecked(review) === 4;
}

export function QualityControl({ scenario, editable, reviewObject }: QualityControlProps) {
  const updateStatusMutation = useUpdateScenarioStatus(scenario.id);
  const intl = useIntl();

  const [review, setReview] = useState<PartialScenarioReview>(
    reviewObject
      ? reviewObject.review
      : {
          content_quality: { comment: "" },
          data_valid: { comment: "" },
          metadata_complete: { comment: "" },
          metadata_valid: { comment: "" },
        }
  );

  const [editing, setEditing] = useState<keyof ScenarioReview | null>(null);

  const amountTrue = numTrue(review);

  const canAccept = editable;
  const canReject = editing === null && amountTrue < 4 && editable;

  const realSetEditing = editable ? setEditing : () => undefined;

  const shortcutAccept = () => {
    if (canAccept) {
      const checkedReview = { ...review };
      checkedReview.content_quality.ok = true;
      checkedReview.data_valid.ok = true;
      checkedReview.metadata_complete.ok = true;
      checkedReview.metadata_valid.ok = true;

      if (allChecked(checkedReview)) {
        updateStatusMutation.mutate({ status: "quality_checked", review: checkedReview });
      }
    }
  };

  const shortcutReject = () => {
    if (amountTrue < 4 && editable) {
      const rejectedReview = { ...review };
      rejectedReview.content_quality.ok = false;
      rejectedReview.data_valid.ok = false;
      rejectedReview.metadata_complete.ok = false;
      rejectedReview.metadata_valid.ok = false;

      if (allChecked(rejectedReview)) {
        updateStatusMutation.mutate({ status: "quality_rejected", review: rejectedReview });
      }
    }
  };

  return (
    <Flex flexDirection="column" alignItems={"flex-start"} px="12px" py={"4px"}>
      <Popover>
        <PopoverTrigger>
          <Button variant="ghost" rightIcon={<ChevronDownIcon />}>
            <FormattedMessage id="qc.header" defaultMessage="Quality Check" />
          </Button>
        </PopoverTrigger>
        <PopoverContent width="500px">
          <PopoverArrow />
          <PopoverCloseButton />
          <PopoverHeader fontWeight="semibold">
            <FormattedMessage id="qc.popover_header" defaultMessage="Quality Checks" />{" "}
            <span style={{ margin: "0 4px 0 8px" }}>{amountTrue}/4</span>
            <CircularProgress
              value={amountTrue * 25}
              size="20px"
              thickness="14px"
              color={amountTrue === 4 ? "green.300" : "bij12_1.700"}
              ml="12px"
            ></CircularProgress>
          </PopoverHeader>
          <PopoverBody>
            {SCENARIO_REVIEW_FIELDS.map((field) =>
              editing === field ? (
                <EditSingleQualityControlItem
                  key={`edit-${field}`}
                  wholeReview={review}
                  field={field}
                  setReview={setReview}
                  stopEditing={() => setEditing(null)}
                />
              ) : (
                <SingleQualityControlItem
                  key={field}
                  wholeReview={review}
                  field={field}
                  setEditing={realSetEditing}
                  editable={editable}
                />
              )
            )}
            {reviewObject ? (
              <Flex alignItems="center">
                <Text fontSize="sm" ml="4px">
                  {intl.formatMessage(
                    { id: "qc.last_updated", defaultMessage: `(last updated by {name})` },
                    { name: reviewObject.updated_by.name }
                  )}
                </Text>
              </Flex>
            ) : null}
            <Flex alignItems="center">
              {canReject && allChecked(review) ? (
                <Button
                  title={intl.formatMessage({ id: "buttons.reject", defaultMessage: "Reject" })}
                  leftIcon={<BiBlock />}
                  onClick={() =>
                    updateStatusMutation.mutate({ status: "quality_rejected", review })
                  }
                >
                  {intl.formatMessage({ id: "buttons.reject", defaultMessage: "Reject" })}
                </Button>
              ) : amountTrue < 4 && editable ? (
                // Short cut reject
                <Button
                  title={intl.formatMessage({
                    id: "buttons.reject_all",
                    defaultMessage: "Shortcut Reject",
                  })}
                  variant="solid"
                  leftIcon={<FiThumbsDown />}
                  onClick={shortcutReject}
                >
                  {intl.formatMessage({
                    id: "message.reject_all",
                    defaultMessage: "Shortcut Reject",
                  })}
                </Button>
              ) : null}
              {canAccept && allChecked(review) ? (
                <Button
                  title={intl.formatMessage({ id: "buttons.accept", defaultMessage: "Accept" })}
                  variant="solid"
                  colorScheme="teal"
                  leftIcon={<FiThumbsUp />}
                  onClick={() => updateStatusMutation.mutate({ status: "quality_checked", review })}
                >
                  {intl.formatMessage({ id: "buttons.accept", defaultMessage: "Accept" })}
                </Button>
              ) : canAccept ? (
                // Short cut accept
                <Button
                  title={intl.formatMessage({
                    id: "buttons.accept_all",
                    defaultMessage: "Shortcut Accept",
                  })}
                  variant="solid"
                  colorScheme="teal"
                  leftIcon={<FiThumbsUp />}
                  onClick={shortcutAccept}
                >
                  {intl.formatMessage({
                    id: "buttons.accept_all",
                    defaultMessage: "Shortcut Accept",
                  })}
                </Button>
              ) : null}
            </Flex>
          </PopoverBody>
        </PopoverContent>
      </Popover>
      <Box
        color="bij12_1.700"
        fontWeight="semibold"
        bgColor={"#fff"}
        fontSize={"sm"}
        rounded="full"
        px="24px"
        py="2px"
        my="6px"
      >
        {amountTrue}/4
        <CircularProgress
          value={amountTrue * 25}
          size="20px"
          thickness="14px"
          color={amountTrue === 4 ? "teal.200" : "bij12_1.700"}
          ml="12px"
        ></CircularProgress>
      </Box>
    </Flex>
  );
}
